import { Injectable } from "@angular/core";
import {
  BfxBaseBlotterService,
  SKIP_INIT_DATA_SERVICE,
} from "../bfx-base-blotter/bfx-base-blotter.service";

import {
  ConnectionMonitorConnectionsTableDefinitionFactory,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
  SystemMonitorLookupService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { moment, SystemMonitorEventImpl } from "shared/cubiq-model-multi-asset";
import {
  SYSMON_EVENT_FQDN,
  SYSMON_EVENT_SOURCE,
  SYSMON_EVENT_STATUS,
  SYSMON_EVENT_STATUS_DISP_TXT,
  SYSMON_EVENT_GROUP_FQDN_DISP_TXT,
  SYSMON_EVENT_FQDN_DISP_TXT,
  SYSMON_EVENT_TIMESTAMP,
  CONNECTION_UP,
  CONNECTION_OUT_OF_SERVICE,
} from "shared/cubiq-services/lookup/model/impl/SystemMonitorLookup.constants";

enum BfxConnectionMonitorConnectionEventStatus {
  UP,
  SCHEDULLED,
  DOWN,
}

export class BfxConnectionMonitorConnectionEvent {
  public eventId: string;
  public fqdn: string;
  public statusType: BfxConnectionMonitorConnectionEventStatus =
    BfxConnectionMonitorConnectionEventStatus.DOWN;
  public status: string;
  public connectionGroup: string;
  public connection: string;
  public session: string;
  public node: string;
  public timestamp: Date;
}

@Injectable()
export class BfxConnectionMonitorConnectionsService extends BfxBaseBlotterService {
  constructor(
    public lookupService: SystemMonitorLookupService,
    public tableDefinitionFactory: ConnectionMonitorConnectionsTableDefinitionFactory,
    public profileActionService: ProfilePromiseActionService,
    public goldenLayoutService: GoldenLayoutService,
    protected userProfileService: BfxUserProfileService,
    protected systemConfigService: SystemConfigPromiseService,
  ) {
    super(SKIP_INIT_DATA_SERVICE, tableDefinitionFactory);
    this.createTableDefinition();
    this.lookupService.isPrimed().then(
      (primed) => {
        this.initData(primed);
      },
      (error) => {
        this.rejectPriming(this._readyResFn, this._readyRejFn, error);
      },
    );
  }

  protected initData(primed: boolean): void {
    if (!primed) {
      this.rejectPriming(
        this._readyResFn,
        this._readyRejFn,
        Error(`${this.constructor.name}.dataService.isPrimed() returned false`),
      );
      return;
    }

    this.subscribeToSubject();

    let rows: any[] = this.getAllRecords()
      .map(this.transformEventNoFail.bind(this))
      .filter((a) => a != null);
    this.onInitialPrime(rows);
    this.resolvePriming(this._readyResFn, this._readyRejFn);
  }

  protected getAllRecords(): SystemMonitorEventImpl[] {
    return this.lookupService.getEvents();
  }

  protected subscribeToSubject(): void {
    this.lookupService.getEventSubject().subscribe(this.processEvent.bind(this));
  }

  protected processEvent(event: SystemMonitorEventImpl): void {
    try {
      let connectionMonitorEvent: BfxConnectionMonitorConnectionEvent =
        this.transformEventNoFail(event);
      if (connectionMonitorEvent != null) {
        this.onUpdateEvent(connectionMonitorEvent);
      }
    } catch (e) {
      logger.out.error(this.constructor.name + ": Failed to process update event: " + e);
    }
  }

  protected addOrUpdateRow(row: any): any {
    if (this._rawData[row.fqdn] != null) {
      super.onDeleteEvent(row.fqdn);
    }

    return super.addOrUpdateRow(row);
  }

  protected transformEventNoFail(
    systemMonitorEvent: SystemMonitorEventImpl,
  ): BfxConnectionMonitorConnectionEvent | null {
    try {
      if (systemMonitorEvent != null) {
        return this.transformEvent(systemMonitorEvent);
      }
    } catch (e) {
      logger.out.error(this.constructor.name + ": Could not parse system monitor event data: " + e);
    }

    return null;
  }

  protected transformEvent(
    systemMonitorEvent: SystemMonitorEventImpl,
  ): BfxConnectionMonitorConnectionEvent {
    let event: BfxConnectionMonitorConnectionEvent = new BfxConnectionMonitorConnectionEvent();

    event.node = systemMonitorEvent.dataMap.get(SYSMON_EVENT_SOURCE);
    event.fqdn = systemMonitorEvent.dataMap.get(SYSMON_EVENT_FQDN);
    event.status = systemMonitorEvent.dataMap.get(SYSMON_EVENT_STATUS_DISP_TXT);
    event.connectionGroup = systemMonitorEvent.dataMap.get(SYSMON_EVENT_GROUP_FQDN_DISP_TXT + ".1");

    if (systemMonitorEvent.dataMap.get(SYSMON_EVENT_GROUP_FQDN_DISP_TXT + ".2") != null) {
      event.connection = systemMonitorEvent.dataMap.get(SYSMON_EVENT_GROUP_FQDN_DISP_TXT + ".2");
      event.session = systemMonitorEvent.dataMap.get(SYSMON_EVENT_FQDN_DISP_TXT);
    } else {
      event.connection = systemMonitorEvent.dataMap.get(SYSMON_EVENT_FQDN_DISP_TXT);
    }

    let timestamp: string = systemMonitorEvent.dataMap.get(SYSMON_EVENT_TIMESTAMP);
    event.timestamp = moment
      .utc(timestamp, SystemMonitorLookupService.serverDateToken)
      .local()
      .toDate();

    // Everything has a red icon unless it's up or out of service
    if (systemMonitorEvent.dataMap.get(SYSMON_EVENT_STATUS) === CONNECTION_UP) {
      event.statusType = BfxConnectionMonitorConnectionEventStatus.UP;
    } else if (systemMonitorEvent.dataMap.get(SYSMON_EVENT_STATUS) === CONNECTION_OUT_OF_SERVICE) {
      event.statusType = BfxConnectionMonitorConnectionEventStatus.SCHEDULLED;
    }

    if (event.connectionGroup == null && event.connection != null) {
      event.connectionGroup = event.connection;
      event.connection = null;
    }

    event.eventId = event.fqdn;
    if (event.node != null && event.node != "??") {
      event.eventId = event.eventId + "_" + event.node;
    }

    return event;
  }
}
